import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { useProgressApiClient } from "@api/progress-api/use-progress-api-client";
import { useProjectApiClient } from "@api/project-api/use-project-api-client";
import { FaroTextButton } from "@components/common/faro-text-button";
import { useFileUploadContext } from "@context-providers/file-upload/file-uploads-context";
import { isStagingAreaUploadTaskContext } from "@custom-types/file-upload-type-guards";
import { BaseProjectProps } from "@custom-types/project-types";
import {
  DEFAULT_CHUNK_SIZE,
  MAX_CONCURRENT_CHUNKS,
  MAX_CONCURRENT_UPLOADS,
  ONE_MEBIBYTE,
} from "@custom-types/upload-manager-types";
import { NO_TRANSLATE_CLASS } from "@faro-lotv/flat-ui";
import { BackgroundTaskState, RevisionStatus } from "@faro-lotv/service-wires";
import { fetchCaptureTreeData } from "@hooks/data-management/use-data-management";
import { useAppNavigation } from "@hooks/navigation/use-app-navigation";
import { useHasUserValidPermissionProjectLevel } from "@hooks/permission-control/use-has-user-valid-permission-project-level";
import { isTaskInProgress } from "@hooks/upload-tasks/upload-tasks-utils";
import { Box, Collapse, Stack } from "@mui/material";
import { InspectButton } from "@pages/data-management/buttons/inspect-button";
import { RetryPublishButton } from "@pages/data-management/buttons/retry-publish-button";
import { DataManagementDotsButton } from "@pages/data-management/data-management-dots-button";
import { DataManagementDropzone } from "@pages/data-management/data-management-dropzone";
import { DataManagementErrorTable, TableType } from "@pages/data-management/data-management-error-table";
import { DataManagementStepper } from "@pages/data-management/data-management-stepper/data-management-stepper";
import { DataManagementTable } from "@pages/data-management/data-management-table";
import {
  AnyFilesInfo,
  StepState,
  StepperIndices,
  TableItem,
  UploadedIdsMap,
  WorkflowState,
  isWorkflowStateError,
} from "@pages/data-management/data-management-types";
import {
  getDeviceType,
  getProcessProgress,
  getRegisterProgress,
  getProgressFromRevisionTask,
  getTableItemsFromEntities,
  getTableItemsFromTasks,
  getUploadProgress,
  getUploadSpeed,
  getUploadTasksToKeepOrRemove,
  getUploadedIdsMap,
  getPublishSettings,
} from "@pages/data-management/data-management-utils";
import { ErrorView } from "@pages/data-management/error-view";
import { AddDataButton } from "@pages/data-management/import-data/add-data-button";
import { cleanupUploadTasks } from "@pages/data-management/import-data/cancel-import-utils";
import { ImportSettingsPage } from "@pages/data-management/import-settings-page";
import { OpenPublishPanelButton } from "@pages/data-management/open-publish-panel-button";
import { OpenViewerButton } from "@pages/data-management/open-viewer-button";
import { PublishPanel } from "@pages/data-management/publish-panel";
import {
  publishedDataSelector,
  isPublishedSelector,
  isPublishingSelector,
  hasPublishErrorSelector,
} from "@pages/data-management/published-data/published-data-selectors";
import { RegisterDataButton } from "@pages/data-management/register-data-button";
import {
  hasRegisterErrorSelector,
  isRegisteredSelector,
  isRegisteringSelector,
  registeredDataSelector,
} from "@pages/data-management/registered-data/registered-data-selectors";
import { RemoveScansDialog } from "@pages/data-management/remove-scans-dialog";
import { UnpublishedChangesChip } from "@pages/data-management/unpublished-changes-chip";
import {
  hasProcessErrorSelector,
  isProcessedSelector,
  isProcessingSelector,
  uploadedDataSelector,
} from "@pages/data-management/uploaded-data/uploaded-data-selectors";
import { supportedDeviceTypes } from "@pages/data-management/uploaded-data/uploaded-data-types";
import { WizardView } from "@pages/data-management/wizard-view";
import { EntityId } from "@reduxjs/toolkit";
import { generateProjectDetailsRoute, getPreservedQueryParams } from "@router/router-helper";
import { ProjectTabs } from "@router/route-params";
import { APITypes } from "@stellar/api-logic";
import { isDevModeEnabledSelector } from "@store/app/app-selector";
import {
  captureTreeForOpenDraftRevisionSelector,
  hasAnyDraftChangesSelector,
  openDraftRevisionSelector,
  readyToPublishPhotogrammetryDraftSelector,
} from "@store/capture-tree/capture-tree-selectors";
import { sdbBackgroundTasksSelector } from "@store/sdb-background-tasks/sdb-background-tasks-selector";
import { selectedSdbCompanyIdSelector } from "@store/sdb-company/sdb-company-selector";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { selectedIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { dataManagementUISelector } from "@store/ui/ui-selector";
import { setHasUnacknowledgedError, setPreviousWorkflowState } from "@store/ui/ui-slice";
import { uploadTasksSelector } from "@store/upload-tasks/upload-tasks-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { colorConst, sphereColors } from "@styles/common-colors";
import { UploadEndpoint } from "@utils/core-file-uploader";
import { RequiredPermissionProjectLevelName } from "@utils/permission-control/project-permission-control-types";
import { useTrackEvent } from "@utils/track-event/use-track-event";
import { DataManagementEvents } from "@utils/track-event/track-event-list";
import { useSettingsApiClient } from "@api/use-settings-api-client";

/** Filter table to keep scans with processing errors. */
function isProcessErrorScan(item: TableItem): boolean {
  return item.state === "error" && item.type === "Scan";
};

/** Filter table to keep all unsupported scans */
function isUnsupportedScan(item: TableItem): boolean {
  const deviceType = getDeviceType(item.deviceType);
  return !supportedDeviceTypes.includes(deviceType);
}

/** The basic view mode of the Data Management workflow. */
type ViewMode = "ERROR" | "DROPZONE" | "WIZARD" | "TABLE";

interface Props extends BaseProjectProps {
  /** Flag whether there was a fetching error and to set the state "startError". */
  hasFetchingError: boolean;
}

/**
 * The main page for the Staging Area workflow, including the progress stepper and the scan table.
 * The code is unfortunately complex and "ugly" since the basic data elements weren't constructed to work
 * well with such a workflow and the user can also add additional data later, resetting the workflow while
 * older data still exists.
 */
export function DataManagementWorkflow({project, hasFetchingError}: Props): JSX.Element {
  const dispatch = useAppDispatch();
  const { previousState, hasUnacknowledgedError } = useAppSelector(dataManagementUISelector);
  const companyId = useAppSelector(selectedSdbCompanyIdSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const { uploadManager } = useFileUploadContext();
  const [isRemoveScansDialogOpen, setIsRemoveScansDialogOpen] = useState(false);
  const [isPublishPanelOpen, setIsPublishPanelOpen] = useState(false);
  const [scansToRemove, setScansToRemove] = useState<TableItem[]>([]);
  const [fileInfos, setFileInfos] = useState<AnyFilesInfo[]>([]);
  const [totalFileSize, setTotalFileSize] = useState<number>(0);
  // Flag whether the browser's file explorer is currently open.
  const [isFileExplorerOpen, setIsFileExplorerOpen] = useState(false);
  // Flag whether file info is currently getting collected and the dropzone should show a spinner.
  const [isDropzoneLoading, setIsDropzoneLoading] = useState<boolean>(false);
  // Flag whether the user has closed the error view. In this case, the table view is shown.
  const [hasClosedErrorView, setHasClosedErrorView] = useState<boolean>(false);
  // Flag whether the user has closed the wizard view. In this case, the table view is shown.
  const [hasClosedWizardView, setHasClosedWizardView] = useState<boolean>(false);
  // Flag whether the user has recently confirmed the "Revert unpublished changes" menu item.
  const [shouldPreventRedirect, setShouldPreventRedirect] = useState<boolean>(false);
  const selectedIds = useAppSelector(selectedIdsSelector("scans"));
  const [visibleTableType, setVisibleTableType] = useState<TableType>("mainTable");
  const [savedSelectedIds, setSavedSelectedIds] = useState<EntityId[]>([]);
  // Flag whether the user has clicked on Revert Unpublished changes.
  // Enables the DROPZONE view so that the spinner can be shown while fetching data from ProjectApi.
  const [isRevertBtnClicked, setIsRevertBtnClicked] = useState(false);
  const [savedSelectedIdsForTable, setSavedSelectedIdsForTable] = useState<{
    [key in TableType]: EntityId[];
  }>({
    mainTable: [],
    errorTable: [],
    unsupportedTable: [],
  });
  const projectApiClient = useProjectApiClient({ projectId: project.id });
  const progressApiClient = useProgressApiClient({ projectId: project.id });
  const settingsApiClient = useSettingsApiClient();
  // Previously, we also used "useBackgroundTasksTracker" to poll for background tasks,
  // but that's already included in fetchCaptureTreeData().
  const sdbBackgroundsTasks = useAppSelector(sdbBackgroundTasksSelector);
  const { navigateToViewer, navigateToInspectAndPublishTool } = useAppNavigation();
  const { trackEvent } = useTrackEvent();

  // ##### Upload tasks ##### //

  const uploadTasksAll = useAppSelector(uploadTasksSelector);
  // uploadTasksSelector also returns tasks for other projects and needs to be filtered.
  // That means we also can't use hasInProgressUploadTasksSelector since it also uses tasks of other projects.
  // We don't want to show tasks from e.g. annotation attachment uploads either. -> Filter by context type.
  const uploadTasks = uploadTasksAll.filter((task) =>
    task.context?.projectId === project.id && isStagingAreaUploadTaskContext(task.context)
  );

  // ##### Uploaded data ##### //

  const uploadedEntities = useAppSelector(uploadedDataSelector);
  const isProcessing = useAppSelector(isProcessingSelector);
  const isProcessed = useAppSelector(isProcessedSelector);
  const hasProcessError = useAppSelector(hasProcessErrorSelector);

  // ##### Photogrammetry draft ##### //

  // For the photogrammetry we don't need registration, result can be directly published.
  const readyToPublishPhotogrammetryDrafts = useAppSelector(readyToPublishPhotogrammetryDraftSelector);

  // ##### Registered data ##### //

  // Revisions created by the RegistrationBackend, from newest to oldest.
  const registrationRevisions = useAppSelector(registeredDataSelector);
  const registrationRevision = registrationRevisions?.[0];
  const isRegistering = useAppSelector(isRegisteringSelector);
  const isRegistered = useAppSelector(isRegisteredSelector);
  const {
    hasRegisterError,
    hasBadRegistration,
    hasPublishError: hasRegistrationPublishError,
  } = useAppSelector(hasRegisterErrorSelector);

  // ##### Draft data ##### //

  const openDraftRevision = useAppSelector(openDraftRevisionSelector);
  const hasAnyDraftChanges = useAppSelector(hasAnyDraftChangesSelector);

  // ##### Published data ##### //

  const publishedRevisions = useAppSelector(publishedDataSelector);
  const publishedRevision = publishedRevisions?.[0];
  const isPublishing = useAppSelector(isPublishingSelector);
  const isPublished = useAppSelector(isPublishedSelector);
  const hasPublishError = useAppSelector(hasPublishErrorSelector);

  // ##### Table items (Part 1) ##### //

  // We don't want to show table entries for (finished) upload tasks for which there's already a member in the
  // uploaded data array.
  const uploadedIdsMap: UploadedIdsMap = useMemo(() => {
    return getUploadedIdsMap(uploadedEntities);
  }, [uploadedEntities]);

  const { uploadTasksToKeep, uploadTasksToRemove } = useMemo(() => {
    return getUploadTasksToKeepOrRemove(
      uploadTasks,
      uploadedIdsMap,
      sdbBackgroundsTasks
    );
  }, [uploadTasks, uploadedIdsMap, sdbBackgroundsTasks]);

  const {
    tableItemsFromTasks,
    hasUploadTaskInProgress,
    hasUploadTaskSucceeded,
    hasUploadTaskWithError,
  } = useMemo(() => {
      return getTableItemsFromTasks(uploadTasksToKeep, currentUser);
  }, [uploadTasksToKeep, currentUser]);

  // ##### Upload ##### //

  const isUploading = hasUploadTaskInProgress ||
  // Use the same check as in hasInProgressUploadTasksSelector here:
    uploadTasksToKeep.some((task) => isTaskInProgress(task)) ||
    // It takes a while until a finished upload task results in an added entity to uploadedEntities.
    // We have to handle this by marking the upload as still in progress for the overall state.
    // But if there's otherwise no task in progress and there's an error, the error takes precedence.
    (hasUploadTaskSucceeded && !hasUploadTaskWithError);
  const isUploaded = !isUploading && (0 < uploadedEntities.length ||
    (0 < uploadTasksToKeep.length && uploadTasksToKeep.every((task) => task.status === BackgroundTaskState.succeeded)));

  // ##### Dev features ##### //

  const isDevModeEnabled = useAppSelector(isDevModeEnabledSelector);
  const { hasUserPermissionProjectLevel } = useHasUserValidPermissionProjectLevel();
  const captureTreeOpenDraft = useAppSelector(captureTreeForOpenDraftRevisionSelector);
  // Given isDevModeEnabled, are the dev tools expanded / fully visible?
  const [areDevToolsExpanded, setAreDevToolsExpanded] = useState(false);
  // Allow to set the maximum number of concurrent file uploads.
  const [maxConcurrentUploads, setMaxConcurrentUploads] = useState<number>(0);
  // Allow to set the maximum number of concurrent chunk uploads per file.
  const [maxConcurrentChunks, setMaxConcurrentChunks] = useState<number>(0);
  // Allow to set the chunk size in MiB.
  const [chunkSizeMib, setChunkSizeMib] = useState<number>(DEFAULT_CHUNK_SIZE / ONE_MEBIBYTE);
  // Allow to set the upload endpoint, overriding the auto-detection.
  const [uploadEndpoint, setUploadEndpoint] = useState<UploadEndpoint>("auto");

  const hasRegularPermission: boolean = useMemo(() => {
      return (
        project.archivingState === APITypes.ArchivingState.UNARCHIVED &&
        hasUserPermissionProjectLevel({
          permissionName: RequiredPermissionProjectLevelName.canViewDataManagement,
          project,
        })
      );
    },
    [hasUserPermissionProjectLevel, project]
  );

  const captureTreeInfo = useCallback(async () => {
    /* eslint-disable no-console -- Dev feature */
    console.log("Fetching capture tree data...");
    const latest = await fetchCaptureTreeData(dispatch, projectApiClient, progressApiClient);
    console.log("Revisions", latest.revisions);
    console.log("Main or Draft entities", latest.mainEntities);
    console.log("Open Draft revision", latest.openDraftRevision);
    console.log("Open Draft entities", latest.openDraftEntities);
    console.log("Open Draft entities (changed)",
      latest.openDraftEntities.filter((entity) => entity.status !== RevisionStatus.initialized)
    );
    /* eslint-enable no-console */
  }, [dispatch, progressApiClient, projectApiClient]);

  /**
   * Toggle the highlight of the "notranslate" class and <var> elements.
   */
  const toggleHighlightLocalization = useCallback(() => {
    const styleId = "notranslate-highlight-style";
    const style = document.getElementById(styleId) as HTMLStyleElement | null;
    if (style) {
      // If style exists, remove it (toggle off)
      style.parentNode?.removeChild(style);
    } else {
      const highlight = "background: rgba(0,255,0,0.2) !important;";
      // If style does not exist, add it (toggle on)
      const style = document.createElement("style");
      style.id = styleId;
      style.innerHTML = `
        .notranslate, var {${highlight}}
        .notranslate > * {${highlight}}
        var > * {${highlight}}`;
      document.head.appendChild(style);
    }
  }, []);

  const captureTreeDiff = useMemo(() => {
    if (!isDevModeEnabled) {
      return "";
    }
    const addedEntities = captureTreeOpenDraft.filter((entity) =>
      entity.status === RevisionStatus.added
    );
    const deletedEntities = captureTreeOpenDraft.filter((entity) =>
      entity.status === RevisionStatus.deleted || entity.status === RevisionStatus.deletedExplicitly
    );
    const modifiedEntities = captureTreeOpenDraft.filter((entity) =>
      entity.status === RevisionStatus.modified
    );
    return `✔ ${addedEntities.length} | ✗ ${deletedEntities.length} | ✎ ${modifiedEntities.length}`;
  }, [captureTreeOpenDraft, isDevModeEnabled]);

  const promptMaxConcurrentUploads = useCallback(() => {
    // For a dev feature, "prompt" is good enough.
    // eslint-disable-next-line no-alert
    const str = window.prompt(
      "Enter the maximum number of concurrent file uploads and chunk uploads per file (integer greater than 0):\n\n" +
        "files, chunks",
      `${maxConcurrentUploads || MAX_CONCURRENT_UPLOADS}, ${maxConcurrentChunks || MAX_CONCURRENT_CHUNKS}`
    );
    if (str) {
      const [files, chunks] = str.trim().split(/[^0-9.]+/).map((s) => parseFloat(s));
      uploadManager.setMaxConcurrentUploads(files, chunks, /* manual */ true);
      // If the function didn't throw, it worked.
      setMaxConcurrentUploads(files);
      setMaxConcurrentChunks(chunks);
    }
  }, [maxConcurrentUploads, maxConcurrentChunks, uploadManager]);

  const promptChunkSizeMib = useCallback(() => {
    // For a dev feature, "prompt" is good enough.
    // eslint-disable-next-line no-alert
    const str = window.prompt(
      "Enter the chunk size in MiB (Default 5):",
      chunkSizeMib.toString()
    );
    if (str) {
      const value = Number(str);
      const chunkSize = value * ONE_MEBIBYTE;
      uploadManager.setChunkSize(chunkSize);
      // If the function didn't throw, it worked.
      setChunkSizeMib(value);
    }
  }, [chunkSizeMib, uploadManager]);

  const promptUploadEndpoint = useCallback(() => {
    // eslint-disable-next-line no-alert
    let str = window.prompt(
      "Enter the desired upload endpoint:\n\n" +
        "a = auto | f = frontdoor | s = storage",
      uploadEndpoint
    );
    if (str) {
      if (str.startsWith("a")) {
        str = "auto";
      } else if (str.startsWith("f")) {
        str = "frontdoor";
      } else if (str.startsWith("s")) {
        str = "storage";
      }
      uploadManager.setUploadEndpoint(str as UploadEndpoint);
      // If the function didn't throw, it worked.
      setUploadEndpoint(str as UploadEndpoint);
    }
  }, [uploadEndpoint, uploadManager]);

  // ##### Determine the state ##### //

  // Determine the overall state.
  // The order must support uploading additional scans after later states have already been reached.
  // So the progress must be set to e.g. uploading although there already is e.g. published data.
  // So we check the four (in-progress, has-error) state pairs in ascending order and afterwards the
  // four "finished" states in descending order.
  let state: WorkflowState = "start";
  let iActiveStep = StepperIndices.upload;

  if (hasFetchingError) {
    state = "startError";
    iActiveStep = StepperIndices.upload;
  } else if (isUploading) {
    state = "uploading";
    iActiveStep = StepperIndices.upload;
  } else if (hasUploadTaskWithError) {
    state = "uploadError";
    iActiveStep = StepperIndices.upload;
  } else if (isProcessing) {
    state = "processing";
    iActiveStep = StepperIndices.process;
  } else if (hasProcessError) {
    state = "processError";
    iActiveStep = StepperIndices.process;
  } else if (isRegistering) {
    state = "registering";
    iActiveStep = StepperIndices.register;
  } else if (hasRegisterError && hasAnyDraftChanges) {
    state = "registerError";
    iActiveStep = StepperIndices.register;
  } else if (hasBadRegistration && hasAnyDraftChanges) {
    state = "registerBadResult";
    iActiveStep = StepperIndices.register;
  } else if (isPublishing) {
    state = "publishing";
    iActiveStep = StepperIndices.publish;
  } else if (hasPublishError || hasRegistrationPublishError) {
    state = "publishError";
    iActiveStep = StepperIndices.publish;
  } else if (isPublished) {
    state = "published";
    iActiveStep = StepperIndices.publish;
  } else if (isRegistered) {
    state = "registered";
    iActiveStep = StepperIndices.publish;
  } else if (isProcessed) {
    state = "processed";
    iActiveStep = StepperIndices.register;
  } else if (isUploaded) {
    state = "uploaded";
    iActiveStep = StepperIndices.process;
  }

  // To simplify debugging in Prod, let's log the most important state information.
  if (isDevModeEnabled) {
    // eslint-disable-next-line no-console -- Dev feature
    console.log("state:", state, "| previousState:", previousState, "| hasAnyDraftChanges:", hasAnyDraftChanges);
  }

  // We need to know if state is "published" before being able to determine the table items based on uploadedEntities.
  const tableItemsFromUploads = useMemo(() => {
    return getTableItemsFromEntities(state, uploadedEntities);
  }, [state, uploadedEntities]);

  const tableItems = useMemo(() => {
    return [ ...tableItemsFromTasks, ...tableItemsFromUploads ];
  }, [tableItemsFromTasks, tableItemsFromUploads]);

  /** Define if we want to show the unsupported scans table */
  const hasUnsupportedScans = useMemo(() => {
    return tableItems.some(isUnsupportedScan);
  }, [tableItems]);

  // Determine the state of the stepper icons.
  let uploadStepState: StepState = "todo";
  if (StepperIndices.process <= iActiveStep) {
    uploadStepState = "done";
  } else if (state === "uploading") {
    uploadStepState = "active";
  } else if (state === "uploadError") {
    uploadStepState = "error";
  }

  let processStepState: StepState = "todo";
  if (StepperIndices.register <= iActiveStep) {
    processStepState = "done";
  } else if (state === "processing") {
    processStepState = "active";
  } else if (state === "processError") {
    processStepState = "error";
  }

  let registerStepState: StepState = "todo";
  if (StepperIndices.publish <= iActiveStep) {
    registerStepState = "done";
  } else if (state === "registering") {
    registerStepState = "active";
  } else if (state === "registerError" || state === "registerBadResult") {
    registerStepState = "error";
  }

  let publishStepState: StepState = "todo";
  if (state === "published") {
    publishStepState = "done";
  } else if (state === "publishing" || (state === "registered" && hasAnyDraftChanges) ||
    readyToPublishPhotogrammetryDrafts.length === 1
  ) {
    publishStepState = "active";
  } else if (state === "publishError") {
    publishStepState = "error";
  }

  // Set the state of the table when fetching is finished.
  useEffect(() => {
    if (state === "processError") {
      setVisibleTableType("errorTable");
    } else if (hasUnsupportedScans) {
      setVisibleTableType("unsupportedTable");
    } else {
      setVisibleTableType("mainTable");
    }
  }, [hasUnsupportedScans, state]);

  // ##### Event handlers ##### //

  /** On enter of the table's collapse/extend action */
  const onEnterTable = useCallback((_nextTable: TableType): void => {
    setSavedSelectedIds(selectedIds);
  }, [selectedIds]);

  /** On exited we restore state of the next table */
  const onExitedTable = useCallback((previousTable: TableType): void => {
    setSavedSelectedIdsForTable((prev) => ({
      ...prev,
      [previousTable]: savedSelectedIds,
    }));
    // Reset the selection on the now visible table
    dispatch(setInitialFetchingItems(savedSelectedIdsForTable[visibleTableType]));
  }, [dispatch, savedSelectedIds, savedSelectedIdsForTable, visibleTableType]);

  /** Depending on the current state, filter the scans for the "mainTable" */
  const isMainTableScan = useCallback((item: TableItem): boolean => {
    if (state === "processError") {
      return !isProcessErrorScan(item) && !isUnsupportedScan(item);
    } else {
      return !isUnsupportedScan(item);
    }
  }, [state]);

  // ##### Progress values ##### //

  const uploadProgress = useMemo(() => {
    const tasks = (state === "uploading" || state === "uploadError") ? uploadTasks : uploadTasksToKeep;
    return getUploadProgress(state, tasks);
  }, [state, uploadTasks, uploadTasksToKeep]);

  const uploadSpeed = useMemo(() => {
    const tasks = (state === "uploading" || state === "uploadError") ? uploadTasks : uploadTasksToKeep;
    return getUploadSpeed(tasks);
  }, [state, uploadTasks, uploadTasksToKeep]);

  const processProgress = useMemo(() => {
    return getProcessProgress(state, captureTreeOpenDraft, sdbBackgroundsTasks);
  }, [state, captureTreeOpenDraft, sdbBackgroundsTasks]);

  const registerProgress = useMemo(() => {
    return getRegisterProgress(state, registrationRevision);
  }, [state, registrationRevision]);

  const publishProgress = useMemo(() => {
    return getProgressFromRevisionTask(publishedRevision);
  }, [publishedRevision]);

  // ##### States of UI elements ##### //

  const isInProgress = state === "uploading" || state === "processing" || state === "registering" || state === "publishing";
  const isEmpty = tableItems.length === 0;

  const hasNewUnacknowledgedError = !hasUnacknowledgedError &&
    (
      state !== previousState &&
      previousState !== undefined &&
      previousState !== "start" &&
      (state === "uploadError" ||
       state === "processError" ||
       state === "registerBadResult" ||
       state === "registerError" ||
       state === "publishError")
    );
  // We must check `isWorkflowStateError` since `hasUnacknowledgedError` may still be true e.g. after deleting a failed scan.
  // The ErrorView would otherwise be incorrectly shown, e.g. in "processed" state, and throw an assertion error.
  // If publishing has failed, always show the error view, since the UI does not indicate any error otherwise.
  const shouldShowErrorView = (
    state === "startError" || state === "publishError" ||
    hasUnacknowledgedError || hasNewUnacknowledgedError
  ) && isWorkflowStateError(state);

  // If you change any of these checks, you must make sure to adjust the button checks below accordingly!
  let viewMode: ViewMode;
  if (state !== "startError" && (isEmpty || fileInfos.length > 0)) {
    // fileInfos can only contain content if a state allowing uploads was active before. We always have to show the
    // dropzone view in this case, even in the theoretical case that uploads would currently be disallowed.
    viewMode = "DROPZONE";
  } else if (isInProgress || (state === "uploaded" && !hasClosedWizardView)) {
    // Due to this check, isInProgress is always false if viewMode is "TABLE" or "ERROR".
    viewMode = "WIZARD";
  } else if (hasClosedErrorView || hasClosedWizardView) {
    viewMode = "TABLE";
  } else if (shouldShowErrorView) {
    viewMode = "ERROR";
  } else {
    viewMode = "TABLE";
  }

  // Once we enter the dropzone view or wizard view again, reset the flag that the user closed the error view.
  useEffect(() => {
    if (viewMode === "DROPZONE" || viewMode === "WIZARD") {
      setHasClosedErrorView(false);
    }
  }, [viewMode]);

  const shouldShowTitle = viewMode === "TABLE";

  // Since viewMode === "TABLE", isInProgress is guaranteed to be false and doesn't need to be checked here.
  // We always allow to upload additional data in the table view,
  // except there are unsupported scans or we are in processError state.
  // The reason is to offer guidance on the necessary steps to resolve an issue that
  // will cause further errors to be shown when actions (such as additional uploads) are taken.
  const shouldShowHiddenDropzone = viewMode === "TABLE" &&
    (state !== "processError" || isDevModeEnabled) &&
    (!hasUnsupportedScans || isDevModeEnabled);
  const shouldShowAddDataBtn = viewMode === "TABLE" &&
    state !== "processError" &&
    !hasUnsupportedScans;
  const shouldShowUnpublishedChanges = hasAnyDraftChanges;
  const shouldShowRegister = viewMode === "TABLE" &&
    (state === "processed" || state === "processError" || state === "registerError");
  const shouldShowInspect = viewMode === "TABLE" &&
    (state === "registerBadResult" || registerStepState === "done");
  const shouldShowRetryPublish = viewMode === "TABLE" && state === "publishError";
  const shouldShowPublish = viewMode === "TABLE" &&
    !shouldShowRetryPublish && (publishStepState === "active" || state === "registerBadResult");
  const shouldShowView = viewMode === "TABLE" && state === "published";
  const shouldShowRepublish = viewMode === "TABLE" && state === "published";
  const shouldShowRevert = (viewMode === "DROPZONE" || viewMode === "TABLE") &&
    hasAnyDraftChanges && !isInProgress && state !== "startError" && state !== "published";
  const shouldShowDots = shouldShowRepublish || shouldShowRevert;
  const shouldShowButtonsForDropzone = viewMode === "DROPZONE" &&
    (shouldShowUnpublishedChanges || shouldShowDots);
  const activityUrl = generateProjectDetailsRoute(
    companyId ?? "",
    project.id,
    ProjectTabs.activity
  ) + getPreservedQueryParams(useLocation().search);

  // TF-2128 set the viewMode to DROPZONE upon clicking revert unpublished scans btn
  // temporary WA to show the spinner in DROPZONE view
  // Future enhancement: need a new viewmode to show the spinner compatible with both TABLE/DROPZONE views
  if (isRevertBtnClicked){
    viewMode="DROPZONE";
  }

  // Trigger the removal of upload tasks for already created entities. That will save us a lot of trouble later when
  // e.g. removing entities so that the outdated associated upload tasks are already gone. But don't remove upload
  // tasks during the uploading step so that the overall progress gets displayed correctly.
  useEffect(() => {
    if (uploadTasksToRemove.length && !(state === "uploading" || state === "uploadError")) {
      cleanupUploadTasks(uploadTasksToRemove, uploadManager);
    }
  }, [state, uploadManager, uploadTasksToRemove]);

  useEffect(() => {
    if (hasNewUnacknowledgedError) {
      dispatch(setHasUnacknowledgedError(true));
    }
  }, [dispatch, hasNewUnacknowledgedError]);

  // ##### Redirect to Viewer on success ##### //

  useEffect(() => {
    dispatch(setPreviousWorkflowState(state));
  }, [dispatch, state]);

  // [TF-2028] Remove dev flag check once manual workflow is fully ready.
  const shouldNavigateToInspect = isDevModeEnabled &&
    !!registrationRevision?.id &&
    state === "registered" &&
    // Only list previous states that are resonable to expect.
    (previousState === "processing" ||
     previousState === "processed" ||
     previousState === "registering" ||
     previousState === "registerBadResult" ||
     previousState === "registerError");

  // Navigate to the Inspect Tool if the workflow type is manual/staged and the registration task finished.
  useEffect(() => {
    if (shouldNavigateToInspect) {
      (async () => {
        // The redirect should only happen for manual/staged workflow type, not auto-publish.
        const { settingWorkflowStopAfter } = await getPublishSettings(project.id, settingsApiClient);
        if (settingWorkflowStopAfter.value === "publish") {
          return;
        }

        trackEvent({
          name: DataManagementEvents.redirectRegister,
          props: {
            projectId: project.id,
            registrationId: registrationRevision?.id,
          },
        });

        const additionalParams = new URLSearchParams({ showRegisterSuccess: "true" });
        navigateToInspectAndPublishTool({
          projectId: project.id,
          registrationId: registrationRevision?.id,
          additionalParams,
        });
      })().catch(() => undefined);
    }
  }, [shouldNavigateToInspect, project.id, registrationRevision?.id,
    settingsApiClient, trackEvent, navigateToInspectAndPublishTool,
  ]);

  let shouldNavigateToViewer = state === "published" &&
    // Only list previous states that are resonable to expect.
    (previousState === "registering" ||
     previousState === "registered" ||
     previousState === "publishing" ||
     previousState === "publishError");

   // If the user recently selected the "Revert unpublished changes" menu item, we must not redirect to the Viewer on
   // state change to "published".
   if (shouldNavigateToViewer && shouldPreventRedirect) {
    shouldNavigateToViewer = false;

    // Unfortunately, we must call setShouldPreventRedirect in a timeout since we would otherwise have the situation in
    // directly following updates that shouldNavigateToViewer is still true, but shouldPreventRedirect is already
    // false. At least it was like that when testing locally (with React's dev mode).
    setTimeout(() => {
      setShouldPreventRedirect(false);
    // eslint-disable-next-line @typescript-eslint/no-magic-numbers -- 10 seconds to be on the safe side
    }, 10000);
  }

  if (shouldNavigateToViewer) {
    trackEvent({
      name: DataManagementEvents.redirectPublish,
      props: {
        projectId: project.id,
      },
    });

    const additionalParams = new URLSearchParams({ showPublishSuccess: "true" });
    navigateToViewer({
      projectId: project.id,
      additionalParams,
    });
  }

  // ##### JSX element ##### //

  const devTools = useMemo(() => {
    return isDevModeEnabled ? (
      <Box
        className={NO_TRANSLATE_CLASS}
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          maxHeight: areDevToolsExpanded ? undefined : "40px",
          maxWidth: areDevToolsExpanded ? undefined : "85px",
          zIndex: 1200,
          padding: "12px",
          margin: "12px",
          borderRadius: "8px",
          color: colorConst.devFeature,
          backgroundColor: "rgba(137, 162, 232, 0.2)",
          backdropFilter: "blur(1px)",
        }}
      >
        <div
          onClick={() => setAreDevToolsExpanded(!areDevToolsExpanded)}
          style={{ cursor: "pointer", marginBottom: areDevToolsExpanded ? "5px" : "40px" }}
          title="Toggle dev tools"
        >Dev tools</div>

        {/* Show warning when viewing read-only in dev mode, since many features won't work. */}
        {!hasRegularPermission &&
          <span style={{ margin: "5px" }}>[READ-ONLY]</span>
        }

        <FaroTextButton
          to={activityUrl}
          target="_blank"
          size="small"
          sx={{ textTransform: "none" }}
          onClick={() => {
            // do nothing, we use "to" to open a new tab
          }}
        >
          Open activity tab
        </FaroTextButton>

        <FaroTextButton
          size="small"
          onClick={captureTreeInfo}
          tooltipText="Click and watch the console output."
        >
          Entities ({openDraftRevision ? `Draft: ${captureTreeDiff}` : "No draft"})
        </FaroTextButton>

        <FaroTextButton
          size="small"
          onClick={toggleHighlightLocalization}
          tooltipText="Click to highlight non-localized elements in green."
        >
          Localization NO_TRANSLATE
        </FaroTextButton>

        {
          shouldShowHiddenDropzone &&
          <AddDataButton
            project={project}
            uploadedIdsMap={uploadedIdsMap}
            setFileInfos={setFileInfos}
            setIsFileExplorerOpen={setIsFileExplorerOpen}
            isDropzoneLoading={isDropzoneLoading}
            setIsDropzoneLoading={setIsDropzoneLoading}
          />
        }

        <InspectButton
          state={state}
          isDevMode={true}
          isFromErrorView={false}
          hasBadRegistration={hasBadRegistration}
          registrationId={registrationRevision?.id}
          projectId={project.id}
        />

        <div style={{ marginBottom: "5px" }}>
          <RegisterDataButton
            state={state}
            caption="Register"
            isDevMode={true}
          />
        </div>

        <div style={{ marginBottom: "5px" }}>
          <OpenPublishPanelButton
            projectId={project.id}
            state={state}
            isDevMode={true}
            setIsPublishPanelOpen={setIsPublishPanelOpen}
          />
        </div>

        <span>Upload settings:</span>

        <FaroTextButton
          size="small"
          onClick={promptMaxConcurrentUploads}
        >
          Concurrent (files, chunks) = ({maxConcurrentUploads || "auto"}, {maxConcurrentChunks || "auto"})
        </FaroTextButton>

        <FaroTextButton
          size="small"
          onClick={promptChunkSizeMib}
        >
          Chunks = {chunkSizeMib} MiB
        </FaroTextButton>

        <FaroTextButton
          size="small"
          onClick={promptUploadEndpoint}
          sx={{ textTransform: "none" }}
        >
          Upload endpoint = {uploadEndpoint}
        </FaroTextButton>

        <div
          onClick={() => setAreDevToolsExpanded(!areDevToolsExpanded)}
          style={{ cursor: "pointer", marginTop: "5px" }}
          title="Toggle dev tools"
        >State: {state}</div>
      </Box>
    ) : undefined;
  }, [
    activityUrl, areDevToolsExpanded, captureTreeDiff, captureTreeInfo, chunkSizeMib, hasBadRegistration, hasRegularPermission,
    isDevModeEnabled, isDropzoneLoading, maxConcurrentChunks, maxConcurrentUploads, openDraftRevision, project,
    promptChunkSizeMib, promptMaxConcurrentUploads, promptUploadEndpoint, registrationRevision?.id, shouldShowHiddenDropzone,
    state, toggleHighlightLocalization, uploadEndpoint, uploadedIdsMap,
  ]);

  if (isPublishPanelOpen) {
    return (
      <>
        { devTools }
        <PublishPanel
          projectId={project.id}
          setIsOpen={setIsPublishPanelOpen}
        />;
      </>
    );
  }

  if (fileInfos.length > 0) {
    return (
      <>
        { devTools }
        <ImportSettingsPage
          project={project}
          uploadedIdsMap={uploadedIdsMap}
          fileInfos={fileInfos}
          setFileInfos={setFileInfos}
          setTotalFileSize={setTotalFileSize}
          isFileExplorerOpen={isFileExplorerOpen}
          setIsFileExplorerOpen={setIsFileExplorerOpen}
          isDropzoneLoading={isDropzoneLoading}
          setIsDropzoneLoading={setIsDropzoneLoading}
        />
      </>
    );
  }

  const regularButtons = (
    <Stack direction="row" sx={{ display: "contents" }}>
      { shouldShowTitle &&
        <div style={{
          display: "flex",
          alignItems: "center",
          float: "left",
          height: "38px",
          fontSize: "20px",
          fontWeight: "600",
          marginLeft: "5px",
          marginRight: "20px",
        }}>
          Scans
        </div>
      }

      { shouldShowUnpublishedChanges &&
        <div style={{
          display: "flex",
          alignItems: "center",
          float: "left",
          height: "38px",
        }}>
          <UnpublishedChangesChip isPublishEnabled={shouldShowPublish} />
        </div>
      }

      <Stack direction="row" sx={{ float: "right", gap: "15px" }}>
        { shouldShowAddDataBtn &&
          <AddDataButton
            project={project}
            uploadedIdsMap={uploadedIdsMap}
            setFileInfos={setFileInfos}
            setIsFileExplorerOpen={setIsFileExplorerOpen}
            isDropzoneLoading={isDropzoneLoading}
            setIsDropzoneLoading={setIsDropzoneLoading}
          />
        }

        { shouldShowRegister &&
          <RegisterDataButton
            state={state}
            caption="Register"
            isDevMode={false}
          />
        }

        { shouldShowInspect &&
          <InspectButton
            projectId={project.id}
            state={state}
            isFromErrorView={false}
            isDevMode={false}
            hasBadRegistration={hasBadRegistration}
            registrationId={registrationRevision?.id}
          />
        }

        { shouldShowRetryPublish &&
          <RetryPublishButton
            projectId={project.id}
            caption="Publish"
            registrationId={registrationRevision?.id}
          />
        }

        { shouldShowPublish &&
          <OpenPublishPanelButton
            projectId={project.id}
            state={state}
            isDevMode={false}
            setIsPublishPanelOpen={setIsPublishPanelOpen}
          />
        }

        { shouldShowView &&
          <OpenViewerButton projectId={project.id} />
        }

        { shouldShowDots &&
          <DataManagementDotsButton
            setIsPublishPanelOpen={setIsPublishPanelOpen}
            projectId={project.id}
            state={state}
            shouldShowRepublish={shouldShowRepublish}
            shouldShowRevert={shouldShowRevert}
            setShouldPreventRedirect={setShouldPreventRedirect}
            isLoading={isDropzoneLoading}
            setIsLoading={setIsDropzoneLoading}
            setIsRevertBtnClicked={setIsRevertBtnClicked}
          />
        }
      </Stack>
    </Stack>
  );

  const Buttons = (
    <Box sx={{
      paddingLeft:   viewMode === "DROPZONE" ? "10px" : "30px",
      paddingRight:  viewMode === "DROPZONE" ? "10px" : "30px",
      paddingTop:    viewMode === "DROPZONE" ?  "0px" : "20px",
      paddingBottom: viewMode === "DROPZONE" ? "15px" :  "8px",
      overflow: "auto",
    }}>
      { regularButtons }
    </Box>
  );

  return (
    <div>
      { devTools }

      <RemoveScansDialog
        projectId={project.id}
        allItems={tableItems}
        items={scansToRemove}
        state={state}
        isOpen={isRemoveScansDialogOpen}
        setIsOpen={setIsRemoveScansDialogOpen}
      />

      <Box
        id="sa-workflow"
        data-testid="sa-workflow"
        sx={{
          display: "inline-block",
          width: "100%",
        }}
      >
        {viewMode === "ERROR" && (
          <Stack>
            {state !== "startError" &&
              <DataManagementStepper
                uploadStepState={uploadStepState}
                processStepState={processStepState}
                registerStepState={registerStepState}
                publishStepState={publishStepState}
              />
            }
            <ErrorView
              project={project}
              state={state}
              registrationId={registrationRevision?.id}
              items={tableItems}
              setHasClosedErrorView={setHasClosedErrorView}
              setShouldPreventRedirect={setShouldPreventRedirect}
              hasUnsupportedScans={hasUnsupportedScans}
            />
          </Stack>
        )}

        {(viewMode === "DROPZONE" || shouldShowHiddenDropzone) && (
          <Stack>
            { shouldShowButtonsForDropzone && Buttons }
            <DataManagementDropzone
              project={project}
              type={viewMode === "DROPZONE" ? "fullscreen" : "overlay"}
              uploadedIdsMap={uploadedIdsMap}
              setFileInfos={setFileInfos}
              isFileExplorerOpen={isFileExplorerOpen}
              setIsFileExplorerOpen={setIsFileExplorerOpen}
              isLoading={isDropzoneLoading}
              setIsLoading={setIsDropzoneLoading}
            />
          </Stack>
        )}

        {viewMode === "WIZARD" && (
          <Stack>
            <DataManagementStepper
              uploadStepState={uploadStepState}
              processStepState={processStepState}
              registerStepState={registerStepState}
              publishStepState={publishStepState}
            />
            <WizardView
              projectId={project.id}
              state={state}
              totalFileSize={totalFileSize}
              uploadSpeed={uploadSpeed}
              uploadProgress={uploadProgress}
              processProgress={processProgress}
              registerProgress={registerProgress}
              publishProgress={publishProgress}
              hasUnsupportedScans={hasUnsupportedScans}
              setHasClosedWizardView={setHasClosedWizardView}
              setShouldPreventRedirect={setShouldPreventRedirect}
            />
          </Stack>
        )}

        {viewMode === "TABLE" && (
          <Box sx={{
            background: "white",
            border: `1px solid ${sphereColors.gray200}`,
          }}>
            { Buttons }
            { state === "processError" && (
              <DataManagementErrorTable
                tableType="errorTable"
                items={tableItems}
                state={state}
                setScansToRemove={setScansToRemove}
                setIsRemoveScansDialogOpen={setIsRemoveScansDialogOpen}
                filter={isProcessErrorScan}
                setVisibleTableType={setVisibleTableType}
                visibleTableType={visibleTableType}
                onEnterTable={onEnterTable}
                onExitedTable={onExitedTable}
              />
            )}

            { hasUnsupportedScans && (
              <DataManagementErrorTable
                tableType="unsupportedTable"
                      items={tableItems}
                      state={state}
                      setScansToRemove={setScansToRemove}
                      setIsRemoveScansDialogOpen={setIsRemoveScansDialogOpen}
                filter={isUnsupportedScan}
                setVisibleTableType={setVisibleTableType}
                visibleTableType={visibleTableType}
                onEnterTable={onEnterTable}
                onExitedTable={onExitedTable}
              />
            )}

            <Collapse in={visibleTableType === "mainTable"} unmountOnExit
              onEnter={() => onEnterTable("mainTable")}
              onExited={() => onExitedTable("mainTable")}
            >
              <Box sx={{ paddingX: "30px" }}>
                <DataManagementTable
                  items={tableItems}
                  state={state}
                  setScansToRemove={setScansToRemove}
                  setIsRemoveScansDialogOpen={setIsRemoveScansDialogOpen}
                  filter={isMainTableScan}
                  sx={{ paddingBottom: "30px" }}
                />
              </Box>
            </Collapse>
          </Box>
        )}
      </Box>
    </div>
  );
}
